import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { Popup } from "antd-mobile"
import styled from "styled-components"

const SWIPE_THRESHOLD = 30
const TAP_MOVE_TOLERANCE = 5

const StyledBottomSheetPopup = styled(Popup)`
  .adm-popup-body {
    background: #0d3144;
    border-radius: 32px 32px 0 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    max-width: var(--app-max-width);
    left: 50%;
    transform: translateX(-50%) !important;
  }
`

const DragZone = styled.div`
  flex-shrink: 0;
  cursor: grab;
  touch-action: none;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`

const DragHandle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 8px;
  height: 24px;
  flex-shrink: 0;
`

const DragHeader = styled.div`
  padding: 0 24px;
  margin-bottom: 8px;
`

const Overlay = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(5, 5, 12, 0.75);
  z-index: 1000;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.3s ease-out;
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
`

const DragIndicator = styled.div`
  width: 50px;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
`

const SheetBody = styled.div<{ $expanded: boolean }>`
  padding: 0 24px;
  padding-bottom: 12px;
  // overflow-y: ${({ $expanded }) => ($expanded ? "auto" : "hidden")};
  flex: 1;
`

interface DraggableBottomSheetProps {
  children: React.ReactNode
  header?: React.ReactNode
  visible?: boolean
  peekHeight?: number
  expandedHeight?: number
  expanded?: boolean
  onExpandChange?: (expanded: boolean) => void
  showOverlay?: boolean
}

const DraggableBottomSheet = ({
  children,
  header,
  visible = true,
  peekHeight = 200,
  expandedHeight,
  expanded,
  onExpandChange,
  showOverlay = true,
}: DraggableBottomSheetProps) => {
  const [internalExpanded, setInternalExpanded] = useState(false)
  const isExpanded = expanded ?? internalExpanded

  const [dragHeight, setDragHeight] = useState<number | null>(null)
  const pointerStartY = useRef(0)
  const startHeight = useRef(0)
  const activePointerId = useRef<number | null>(null)
  const hasMoved = useRef(false)

  const dragZoneRef = useRef<HTMLDivElement>(null)
  const sheetBodyRef = useRef<HTMLDivElement>(null)
  const [measuredExpandedHeight, setMeasuredExpandedHeight] = useState<
    number | null
  >(null)

  useLayoutEffect(() => {
    const dragZoneH = dragZoneRef.current?.offsetHeight ?? 0
    const bodyScrollH = sheetBodyRef.current?.scrollHeight ?? 0
    setMeasuredExpandedHeight(dragZoneH + bodyScrollH)
  }, [children, header, peekHeight])

  const effectiveExpandedHeight =
    expandedHeight ?? measuredExpandedHeight ?? peekHeight

  const updateExpanded = (value: boolean) => {
    setInternalExpanded(value)
    onExpandChange?.(value)
  }

  // Sync with controlled prop
  useEffect(() => {
    if (expanded !== undefined && expanded !== internalExpanded) {
      setInternalExpanded(expanded)
    }
  }, [expanded])

  const onPointerDown = (e: React.PointerEvent) => {
    if (!e.isPrimary) return
    e.preventDefault()

    const target = e.currentTarget as HTMLElement
    target.setPointerCapture(e.pointerId)

    activePointerId.current = e.pointerId
    pointerStartY.current = e.clientY
    startHeight.current = isExpanded ? effectiveExpandedHeight : peekHeight
    hasMoved.current = false
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (e.pointerId !== activePointerId.current) return

    const deltaY = pointerStartY.current - e.clientY

    if (!hasMoved.current && Math.abs(deltaY) > TAP_MOVE_TOLERANCE) {
      hasMoved.current = true
    }

    if (hasMoved.current) {
      const newHeight = startHeight.current + deltaY
      setDragHeight(
        Math.max(peekHeight, Math.min(newHeight, effectiveExpandedHeight)),
      )
    }
  }

  const onPointerUp = (e: React.PointerEvent) => {
    if (e.pointerId !== activePointerId.current) return
    activePointerId.current = null

    const deltaY = pointerStartY.current - e.clientY

    if (hasMoved.current) {
      if (deltaY > SWIPE_THRESHOLD) {
        updateExpanded(true)
      } else if (deltaY < -SWIPE_THRESHOLD) {
        updateExpanded(false)
      }
    } else {
      updateExpanded(!isExpanded)
    }

    setDragHeight(null)
  }

  const onPointerCancel = (e: React.PointerEvent) => {
    if (e.pointerId !== activePointerId.current) return
    activePointerId.current = null
    setDragHeight(null)
  }

  const isDragging = dragHeight !== null
  const height = isDragging
    ? dragHeight
    : isExpanded
    ? effectiveExpandedHeight
    : peekHeight

  return (
    <>
      {showOverlay && (
        <Overlay $visible={isExpanded} onClick={() => updateExpanded(false)} />
      )}
      <StyledBottomSheetPopup
        visible={visible}
        position="bottom"
        mask={false}
        closeOnSwipe={false}
        disableBodyScroll={false}
        getContainer={null}
        destroyOnClose={false}
        forceRender
        bodyStyle={{
          height,
          transition: isDragging ? "none" : "height 0.3s ease-out",
        }}
      >
        <DragZone
          ref={dragZoneRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
        >
          <DragHandle>
            <DragIndicator />
          </DragHandle>
          {header && <DragHeader>{header}</DragHeader>}
        </DragZone>
        <SheetBody
          ref={sheetBodyRef}
          $expanded={isExpanded}
          onClick={() => {
            if (!isExpanded) {
              updateExpanded(true)
            }
          }}
        >
          {children}
        </SheetBody>
      </StyledBottomSheetPopup>
    </>
  )
}

export default DraggableBottomSheet
